import { Button, Card, DateInput, Input, Label, NumberInput, Select } from "components";
import { Controller, useFormContext } from "react-hook-form";
import { FormType, PicturesType } from "../../types";
import type { ProductType } from "../../types/ProductType";
import moment from "moment";
import { Link } from "react-router-dom";
import { PicturesSection } from "../PicturesSection";
import { useDropdownLocation, useProduct } from "../../utils";
import { useProductDropdown } from "../../utils/hooks/useProductDropdown";
import { Fragment, useEffect, useMemo, useState } from "react";
import { AddStock } from "../AddStock";

export type OpeningStockDetailFallback = Pick<
    FormType,
    "product_id" | "product_name" | "product_barcode" | "unit_name" | "picture"
>

export function FormSection(props: {
    isEdit?: boolean
    created?: boolean
    /** Baris opening stock (getbyid) untuk tampilan saat produk tidak ada di dropdown / mode edit */
    openingStockDetail?: OpeningStockDetailFallback | null
}) {
    // hook-form
    const { control, watch, setValue } = useFormContext<FormType>()
    const productId = watch('product_id')

    // hooks
    const { dropdownLocation } = useDropdownLocation()
    const { dropdownProduct } = useProductDropdown()
    const { dataProduct, refetch } = useProduct()

    // state
    const [pictureValue, setPictureValue] = useState<PicturesType[]>([])
    const [description, setDescription] = useState<string>('')

    const SelectedProduct = dataProduct.find(value => value.product_id.toString() === productId.toString())

    const productSelectOptions = useMemo(() => {
        const d = props.openingStockDetail
        if (!d?.product_id || !productId || String(d.product_id) !== String(productId)) {
            return dropdownProduct
        }
        if (dropdownProduct.some((item) => item.value === String(d.product_id))) {
            return dropdownProduct
        }
        const synthetic: ProductType & { label: string; value: string } = {
            product_id: Number(d.product_id),
            product_name: d.product_name,
            product_barcode: d.product_barcode,
            mr_unit_id: "",
            mr_unit_name: d.unit_name ?? "",
            price: "",
            av_price: "",
            picture: d.picture ?? [],
            coa_code: "",
            coa_name: "",
            label: `${d.product_barcode} - ${d.product_name}`,
            value: String(d.product_id),
        }
        return [synthetic, ...dropdownProduct]
    }, [dropdownProduct, props.openingStockDetail, productId])

    useEffect(() => {
        if (SelectedProduct && props?.created) {
            setValue("mr_unit_id", SelectedProduct?.mr_unit_name ?? "")
            setValue("av_price_unit", SelectedProduct?.av_price ?? "0")
        }
        if (SelectedProduct) {
            setPictureValue(SelectedProduct?.picture ?? [])
            setDescription(SelectedProduct?.product_name ?? "")
        } else if (
            props.openingStockDetail &&
            String(props.openingStockDetail.product_id) === String(productId)
        ) {
            setPictureValue(props.openingStockDetail.picture ?? [])
            setDescription(props.openingStockDetail.product_name ?? "")
        }
        /* eslint-disable */
    }, [productId, dataProduct, props?.created, SelectedProduct, props.openingStockDetail, setValue])

    

    return (
        <Fragment>
            <div>
                <div className="w-full mb-2">
                    <Card>
                        <Card.Body>
                            <div className="flex justify-between items-center">
                                <div className="lg:flex">
                                    <Label text="DATE OPENING STOCK" className="lg:w-[180px]" />
                                    <div className="lg:w-[200px] w-[300px]">
                                        <Controller
                                            control={control}
                                            name="transaction_date"
                                            render={({ field, fieldState }) => (
                                                <DateInput
                                                    placeholderText="Select Date"
                                                    error={fieldState.error?.message}
                                                    ref={field.ref}
                                                    selected={moment(field.value).toDate()}
                                                    onChange={value => field.onChange(moment(value).format("YYYY-MM-DD"))}
                                                    disabled={!props.isEdit}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                {props.isEdit && (
                                    <Link to='/setup/inventory/opening-stock'>
                                        <Button color="primary">BACK</Button>
                                    </Link>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <div>
                    <Card>
                        <Card.Body>
                            <div className="lg:mb-3 lg:grid lg:grid-cols-3">
                                <div className="lg:flex items-center gap-2">
                                    <Label text="LOCATION" />
                                    <Controller
                                        control={control}
                                        name="location_id"
                                        render={({ field, fieldState }) => (
                                            <Select
                                                className="lg:w-[205px] w-full"
                                                options={dropdownLocation}
                                                placeholder="Select Location"
                                                ref={field.ref}
                                                isDisabled={!props.isEdit}
                                                error={fieldState?.error?.message}
                                                value={dropdownLocation.find(item => item.value === field.value.toString()) ?? null}
                                                onChange={item => {
                                                    field.onChange(item?.value)
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="lg:flex items-center gap-5">
                                    <Label text="ITEM" />
                                    <Controller
                                        control={control}
                                        name="product_id"
                                        render={({ field, fieldState }) => (
                                            <Select
                                                className="lg:w-[205px]"
                                                options={productSelectOptions}
                                                placeholder="Select Item"
                                                ref={field.ref}
                                                error={fieldState?.error?.message}
                                                value={productSelectOptions.find(item => item.value === field.value.toString()) ?? null}
                                                isDisabled={!props.isEdit}
                                                onChange={item => {
                                                    field.onChange(item?.value)
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="lg:flex items-center gap-2">
                                    <Label text="DESCRIPTION" />
                                    <Input
                                        className="lg:w-[205px]"
                                        disabled
                                        value={description}
                                    />
                                </div>
                            </div>
                            <div className="lg:grid lg:grid-cols-3">
                                <div className="lg:flex items-center mb-3 gap-4">
                                    <Label text="AV PRICE" />
                                    <Controller
                                        control={control}
                                        name="av_price_unit"
                                        render={({ field, fieldState }) => (
                                            <NumberInput
                                                inputClass="lg:w-[205px]"
                                                decimalScale={2}
                                                error={fieldState.error?.message}
                                                ref={field.ref}
                                                value={field.value}
                                                decimalSeparator='.'
                                                thousandSeparator=","
                                                placeholder="Enter Price"
                                                onValueChange={value => field.onChange(value.floatValue)}
                                                disabled={!props.isEdit}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="lg:flex items-center mb-3 gap-2">
                                    <Label text="STOCK" />
                                    <Controller
                                        control={control}
                                        name="soh"
                                        render={({ field, fieldState }) => (
                                            <NumberInput
                                                inputClass="lg:w-[205px]"
                                                decimalScale={2}
                                                error={fieldState.error?.message}
                                                ref={field.ref}
                                                value={field.value}
                                                placeholder="Enter Stock"
                                                onValueChange={value => field.onChange(value.floatValue)}
                                                disabled={!props.isEdit}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="lg:flex items-center gap-2">
                                    <Label text="UNIT" className="mr-[55px]" />
                                    <Controller
                                        control={control}
                                        name="mr_unit_id"
                                        render={({ field, fieldState }) => (
                                            <Input
                                                className="lg:w-[205px] mb-2"
                                                ref={field.ref}
                                                disabled
                                                placeholder="Enter Unit"
                                                error={fieldState?.error?.message}
                                                value={
                                                    SelectedProduct?.mr_unit_name ??
                                                    props.openingStockDetail?.unit_name ??
                                                    ""
                                                }
                                            />
                                        )}
                                    />
                                    {props.isEdit && (
                                         <div className="mr-2 flex justify-end">
                                         <AddStock id={productId} refetch={refetch} />
                                     </div>
                                    )}
                                   
                                </div>
                            </div>
                            <div >
                                <Label text="PICTURES" />
                                <PicturesSection items={5} dataPicture={pictureValue} className="h-[150px] w-full" />
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </Fragment>
    )
}